import { createApi } from '@reduxjs/toolkit/query/react'
import { GenericResponse } from '../types/global.d'
import {
    LoginRequest,
    RegisterRequest,
    ForgotPasswordRequest,
    ChangePasswordRequest,
    ResetPasswordRequest,
    ScopeContext,
    ScopeRoles
} from '../types/auth.d'
import { filteredFilter } from '../utils/api';
import { authBaseQuery } from '../app/axiosBaseQuery'
import {logout, setAccessToken} from '../app/slices/sessionSlice';

export const AUTH_API_REDUCER_KEY = 'auth';
export const AUTH_PATH = 'auth';

export const authApi = createApi({
    reducerPath: AUTH_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    endpoints: (builder) => ({
        registerUser: builder.mutation<GenericResponse, RegisterRequest>({
            query(data) {
                return {
                    url: `/${ AUTH_PATH }/register`,
                    method: 'POST',
                    data,
                };
            },
        }),
        loginUser: builder.mutation<
            { access_token: string; status: string },
            LoginRequest
        >({
            query(login) {
                return {
                    url: `/${ AUTH_PATH }/login`,
                    method: 'POST',
                    data: login,
                };
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setAccessToken(data.access_token));
                } catch (error) {
                    console.warn('Cannot login user', error);
                }
            },
        }),
        refresh: builder.mutation<
            { access_token: string; status: string },
            { scope_group: string; reset: boolean; }
        >({
            query({scope_group, reset = false}) {

                const queryParams: Record<string, any> =  Object.assign({}, filteredFilter({
                    scope_group,
                    reset
                }))

                return {
                    url: `/${ AUTH_PATH }/refresh?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                };
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setAccessToken(data.access_token));
                } catch (error) {
                    console.warn('Cannot refresh user', error);
                }
            },
        }),
        changePassword: builder.mutation<GenericResponse, ChangePasswordRequest>({
            query(data) {
                return {
                    url: `/${ AUTH_PATH }/change-password`,
                    method: 'POST',
                    data,
                };
            },
        }),
        logoutUser: builder.mutation<void, void>({
            query() {
                return {
                    url: `/${ AUTH_PATH }/logout`,
                    method: 'POST',
                };
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(logout());
                } catch (error) {
                    console.warn('Cannot remove user', error);
                }
            },
        }),
        activateUser: builder.mutation<GenericResponse, string>({
            query(activationToken) {
                return {
                    url: `/${ AUTH_PATH }/activate?activation_token=${activationToken}`,
                    method: 'POST',
                };
            },
        }),
        forgotPassword: builder.mutation<GenericResponse, ForgotPasswordRequest>({
            query(data) {
                return {
                    url: `/${ AUTH_PATH }/forgot-password`,
                    method: 'POST',
                    data,
                };
            },
        }),
        resetPassword: builder.mutation<GenericResponse, ResetPasswordRequest>({
            query({ resetToken, newPassword, newPasswordConfirm }) {
                return {
                    url: `/${ AUTH_PATH }/reset-password?reset_token=${resetToken}`,
                    method: 'POST',
                    data: { newPassword, newPasswordConfirm },
                };
            },
        }),
        getSupportedScopeContexts: builder.query<ScopeRoles[], null>({
            queryFn: async (_id, _api, _extraOptions, baseQuery) => {

                const response = await baseQuery({
                    url: `/${ AUTH_PATH }/supported-scope-contexts`,
                    method: 'GET',
                });

                return response;
            },
            providesTags: ['SupportedScopeContexts'],
        }),
    }),
});

export const {
    useLoginUserMutation,
    useRegisterUserMutation,
    useChangePasswordMutation,
    useLogoutUserMutation,
    useRefreshMutation,
    useActivateUserMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useGetSupportedScopeContextsQuery,
} = authApi;
